/**
 * Home Reel Carousel
 * Custom carousel for home page that:
 * - Cycles through slides automatically on first visit
 * - On return visits, shows last slide with intro visible (no animation)
 * - Doesn't respond to mouse/touch interactions
 * - Stops after completing all slides
 * - Transitions background color with each slide
 */

// Use sessionStorage to track if carousel has completed
const CAROUSEL_VIEWED_KEY = 'homeReelViewed';

export function initHomeReelCarousel() {
  const container = document.querySelector('.home-reel__carousel');
  if (!container || container.getAttribute('data-carousel-initialized') === 'true') {
    return;
  }

  const homeReel = document.querySelector('.home-reel');
  const track = container.querySelector('.carousel-track');
  const slides = Array.from(container.querySelectorAll('.carousel-slide'));

  if (!homeReel || !track || slides.length === 0) {
    return;
  }

  // Mark as initialized
  container.setAttribute('data-carousel-initialized', 'true');

  // Configuration
  const duration = parseInt(container.getAttribute('data-duration')) || 4500;
  const totalSlides = slides.length;

  // Check if user has already viewed the carousel this session.
  const hasViewedCarousel = sessionStorage.getItem(CAROUSEL_VIEWED_KEY) === 'true';

  // If returning visitor (non-looping only), show last slide with intro immediately
  if (hasViewedCarousel) {
    // Remove is-active from all slides
    slides.forEach((slide) => slide.classList.remove('is-active'));

    // Activate last slide
    const lastSlide = slides[totalSlides - 1];
    lastSlide.classList.add('is-active');

    // Set background color from last slide
    const bgColor = lastSlide.getAttribute('data-bg-color');
    if (bgColor && homeReel) {
      homeReel.style.backgroundColor = bgColor;
    }

    // Show intro immediately
    const intro = homeReel.querySelector('.home-reel__intro');
    if (intro) {
      intro.classList.add('is-visible');
    }

    // Disable interactions
    track.style.pointerEvents = 'none';
    track.style.userSelect = 'none';

    return; // Don't start autoplay
  }

  let currentIndex = 0;
  let pendingAdvance = null;

  // Videos in this carousel play once and drive slide advancement via 'ended',
  // so strip any loop attribute the template rendered.
  container.querySelectorAll('video').forEach((v) => v.removeAttribute('loop'));

  // Ensure first slide is active
  slides[0].classList.add('is-active');

  // Go to specific slide
  function goToSlide(index) {
    if (index < 0 || index >= slides.length) return;

    // Remove active class from all slides
    slides.forEach((slide) => {
      slide.classList.remove('is-active');
    });

    // Add active class to target slide
    slides[index].classList.add('is-active');

    // Update .home-reel background colour from slide's data-bg-color
    const bgColor = slides[index].getAttribute('data-bg-color');
    if (bgColor && homeReel) {
      homeReel.style.backgroundColor = bgColor;
    }

    currentIndex = index;
  }

  // Navigate to next slide
  function nextSlide() {
    const nextIndex = currentIndex + 1;

    // If we've reached the end, stop on the last slide and show intro
    if (nextIndex >= totalSlides) {
      stopAutoplay();
      showIntro();
      return;
    }

    goToSlide(nextIndex);
    scheduleAdvance();
  }

  // Show intro text and mark carousel as viewed
  function showIntro() {
    const intro = homeReel.querySelector('.home-reel__intro');
    if (intro) {
      intro.classList.add('is-visible');
    }
    // Store that carousel has been viewed this session
    sessionStorage.setItem(CAROUSEL_VIEWED_KEY, 'true');
  }

  // Schedule the next advance based on the current slide's contents.
  // Video slides wait for every <video> in the slide to fire 'ended'; slides
  // with only images fall back to the configured duration.
  function scheduleAdvance() {
    cancelPendingAdvance();

    const slide = slides[currentIndex];
    const videos = Array.from(slide.querySelectorAll('video'));

    if (videos.length === 0) {
      pendingAdvance = { timerId: setTimeout(nextSlide, duration) };
      return;
    }

    const remaining = new Set(videos);
    const listeners = [];

    videos.forEach((video) => {
      const onEnded = () => {
        remaining.delete(video);
        if (remaining.size === 0) nextSlide();
      };
      video.addEventListener('ended', onEnded);
      listeners.push([video, onEnded]);
      // Rewind so re-entering the carousel starts the clip cleanly.
      try { video.currentTime = 0; } catch (_) { /* not seekable yet */ }
      const playAttempt = video.play();
      if (playAttempt && typeof playAttempt.catch === 'function') {
        playAttempt.catch(() => {});
      }
    });

    pendingAdvance = { listeners };
  }

  function cancelPendingAdvance() {
    if (!pendingAdvance) return;
    if (pendingAdvance.timerId) clearTimeout(pendingAdvance.timerId);
    if (pendingAdvance.listeners) {
      pendingAdvance.listeners.forEach(([video, fn]) => {
        video.removeEventListener('ended', fn);
      });
    }
    pendingAdvance = null;
  }

  // Preserve the old name so external callers (e.g. Swup cleanup) keep working.
  function stopAutoplay() {
    cancelPendingAdvance();
  }

  // Disable all mouse/touch interactions
  track.style.pointerEvents = 'none';
  track.style.userSelect = 'none';

  // Start the carousel
  scheduleAdvance();

  // Store cleanup function for external access
  container._stopAutoplay = stopAutoplay;
}
